import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { TopBar } from "./components/layout/TopBar";
import { ScrollToTop } from "./components/layout/ScrollToTop";
import { Footer } from "./components/layout/Footer";
import { AuthenticatedLayout } from "./components/layout/AuthenticatedLayout";
import { LoginPage } from "./pages/LoginPage";
import { VoicemailPage } from "./pages/VoicemailPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { DashboardPage } from "./pages/DashboardPage";
import { ApplicationsPage } from "./pages/ApplicationsPage";
import { ApplicationDetailPage } from "./pages/ApplicationDetailPage";
import {
  ApplicationVoicePage,
  GlobalVoicePage,
  VoiceDetailPage,
} from "./pages/ApplicationVoicePage";
import { ApplicationVariablesPage } from "./pages/ApplicationVariablesPage";
import { FlowDesignerPage } from "./pages/FlowDesignerPage";
import { HuntGroupsPage } from "./pages/HuntGroupsPage";
import { HuntGroupDetailPage } from "./pages/HuntGroupDetailPage";
import { DestinationsPage } from "./pages/DestinationsPage";
import { DestinationDetailPage } from "./pages/DestinationDetailPage";
import { NumbersPage } from "./pages/NumbersPage";
import { NumberEditPage } from "./pages/NumberEditPage";
import { BlacklistsPage } from "./pages/BlacklistsPage";
import { BlacklistEditPage } from "./pages/BlacklistEditPage";
import { SchedulesPage } from "./pages/SchedulesPage";
import { WeekplanDetailPage } from "./pages/WeekplanDetailPage";
import { CalendarDetailPage } from "./pages/CalendarDetailPage";
import { DataSheetsPage } from "./pages/DataSheetsPage";
import { DataSheetSchemaPage } from "./pages/DataSheetSchemaPage";
import { DataSheetRecordsPage } from "./pages/DataSheetRecordsPage";
import { LiveCallsReportPage } from "./pages/LiveCallsReportPage";
import { CallReportPage } from "./pages/CallReportPage";
import { RecordingsReportPage } from "./pages/RecordingsReportPage";
import { CdrReportPage } from "./pages/CdrReportPage";
import { CustomReportPage } from "./pages/CustomReportPage";
import { ReportsAdminPage } from "./pages/admin/ReportsAdminPage";
import { ReportEditPage } from "./pages/admin/ReportEditPage";
import { FonixEntryImportPage } from "./pages/admin/FonixEntryImportPage";
import { PbxRoutingPage } from "./pages/ippbx/PbxRoutingPage";
import { PbxExtensionsPage } from "./pages/ippbx/PbxExtensionsPage";
import { PbxExtensionDetailPage } from "./pages/ippbx/PbxExtensionDetailPage";
import { PbxBanlistPage } from "./pages/ippbx/PbxBanlistPage";
import { ManagersAdminPage } from "./pages/admin/ManagersAdminPage";
import { ManagerEditPage } from "./pages/admin/ManagerEditPage";
import { CustomersAdminPage } from "./pages/admin/CustomersAdminPage";
import { CustomerEditPage } from "./pages/admin/CustomerEditPage";
import { PermissionGroupsPage } from "./pages/admin/PermissionGroupsPage";
import { AdminNumbersPage } from "./pages/admin/AdminNumbersPage";
import { AdminNumberEditPage } from "./pages/admin/AdminNumberEditPage";
import { AiAgentsAdminPage } from "./pages/admin/AiAgentsAdminPage";
import { RecognizersAdminPage } from "./pages/admin/RecognizersAdminPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import type { SessionKind } from "./api/authApi";
import { SessionProvider } from "./hooks/useSession";

function Protected({
  children,
  requireKind,
  requireAnyKind,
}: {
  children: React.ReactNode;
  requireKind?: SessionKind;
  requireAnyKind?: SessionKind[];
}) {
  return (
    <ProtectedRoute requireKind={requireKind} requireAnyKind={requireAnyKind}>
      <AuthenticatedLayout>{children}</AuthenticatedLayout>
    </ProtectedRoute>
  );
}

export default function App() {
  const location = useLocation();
  // The public voicemail page (/vm/<guid>, opened from a mail without a
  // login) carries its own GlobalXess header and footer.
  const bare = location.pathname.startsWith("/vm/");
  return (
    <SessionProvider>
      <div className="app-shell">
        <ScrollToTop />
        {!bare && <TopBar />}
        <main className="app-main">
          <Routes>
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/vm/:key" element={<VoicemailPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/home" element={<Navigate to="/dashboard" replace />} />
            <Route
              path="/dashboard"
              element={
                <Protected>
                  <DashboardOrAdminLanding />
                </Protected>
              }
            />
            <Route
              path="/voice/:voiceId"
              element={
                <Protected>
                  <VoiceDetailPage />
                </Protected>
              }
            />
            <Route
              path="/voice"
              element={
                <Protected>
                  <GlobalVoicePage />
                </Protected>
              }
            />
            <Route
              path="/applications"
              element={
                <Protected>
                  <ApplicationsPage />
                </Protected>
              }
            />
            {/* The /:applicationId/design route is registered before the
                bare /:applicationId so the literal "design" segment is
                always matched over the parametric detail page. */}
            <Route
              path="/applications/:applicationId/design"
              element={
                <ProtectedRoute>
                  <FlowDesignerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/applications/:applicationId/voice/:voiceId"
              element={
                <Protected>
                  <VoiceDetailPage />
                </Protected>
              }
            />
            <Route
              path="/applications/:applicationId/voice"
              element={
                <Protected>
                  <ApplicationVoicePage />
                </Protected>
              }
            />
            <Route
              path="/applications/:applicationId/variables"
              element={
                <Protected>
                  <ApplicationVariablesPage />
                </Protected>
              }
            />
            <Route
              path="/applications/:applicationId"
              element={
                <Protected>
                  <ApplicationDetailPage />
                </Protected>
              }
            />
            <Route
              path="/huntgroups"
              element={
                <Protected>
                  <HuntGroupsPage />
                </Protected>
              }
            />
            <Route
              path="/huntgroups/:huntGroupId"
              element={
                <Protected>
                  <HuntGroupDetailPage />
                </Protected>
              }
            />
            <Route
              path="/destinations"
              element={
                <Protected>
                  <DestinationsPage />
                </Protected>
              }
            />
            <Route
              path="/destinations/:destinationId"
              element={
                <Protected>
                  <DestinationDetailPage />
                </Protected>
              }
            />
            <Route
              path="/numbers"
              element={
                <Protected>
                  <NumbersPage />
                </Protected>
              }
            />
            {/* Blacklist routes are registered before the parametric
                /numbers/:dnisId so the literal path wins. */}
            <Route
              path="/numbers/blacklists"
              element={
                <Protected>
                  <BlacklistsPage />
                </Protected>
              }
            />
            <Route
              path="/numbers/blacklists/:blacklistId"
              element={
                <Protected>
                  <BlacklistEditPage />
                </Protected>
              }
            />
            <Route
              path="/numbers/:dnisId"
              element={
                <Protected>
                  <NumberEditPage />
                </Protected>
              }
            />
            <Route
              path="/schedules"
              element={
                <Protected>
                  <SchedulesPage />
                </Protected>
              }
            />
            <Route
              path="/schedules/weekplans/:weekplanId"
              element={
                <Protected>
                  <WeekplanDetailPage />
                </Protected>
              }
            />
            <Route
              path="/schedules/calendars/:calendarId"
              element={
                <Protected>
                  <CalendarDetailPage />
                </Protected>
              }
            />
            <Route
              path="/data"
              element={
                <Protected>
                  <DataSheetsPage />
                </Protected>
              }
            />
            {/* The /:sheetId/records route is registered before the bare
                /:sheetId so the literal "records" segment is always
                matched over the parametric schema-editor route. */}
            <Route
              path="/data/:sheetId/records"
              element={
                <Protected>
                  <DataSheetRecordsPage />
                </Protected>
              }
            />
            <Route
              path="/data/:sheetId"
              element={
                <Protected>
                  <DataSheetSchemaPage />
                </Protected>
              }
            />
            <Route
              path="/reports/live/calls"
              element={
                <Protected>
                  <LiveCallsReportPage />
                </Protected>
              }
            />
            <Route
              path="/reports/cdr"
              element={
                <Protected>
                  <CdrReportPage />
                </Protected>
              }
            />
            <Route
              path="/reports/recordings"
              element={
                <Protected>
                  <RecordingsReportPage />
                </Protected>
              }
            />
            {/* Dynamic (admin-built) reports; static path, so before /reports/:direction. */}
            <Route
              path="/reports/custom/:reportName"
              element={
                <Protected>
                  <CustomReportPage />
                </Protected>
              }
            />
            {/* Call reports: /reports/inbound|outbound (days) → /:date (hours)
                → /:date/:hour (calls). The static /reports/live/calls route
                above still wins over the :direction/:date pattern. */}
            <Route
              path="/reports/:direction"
              element={
                <Protected>
                  <CallReportPage />
                </Protected>
              }
            />
            <Route
              path="/reports/:direction/:date"
              element={
                <Protected>
                  <CallReportPage />
                </Protected>
              }
            />
            <Route
              path="/reports/:direction/:date/:hour"
              element={
                <Protected>
                  <CallReportPage />
                </Protected>
              }
            />
            <Route
              path="/admin/customers"
              element={
                <Protected requireAnyKind={["Admin", "Manager"]}>
                  <CustomersAdminPage />
                </Protected>
              }
            />
            {/* Both /admin/customers/new (create) and /admin/customers/:id
                (edit) resolve here; the page branches on the "new" param. */}
            <Route
              path="/admin/customers/:customerId"
              element={
                <Protected requireAnyKind={["Admin", "Manager"]}>
                  <CustomerEditPage />
                </Protected>
              }
            />
            <Route
              path="/admin/numbers"
              element={
                <Protected requireKind="Admin">
                  <AdminNumbersPage />
                </Protected>
              }
            />
            <Route
              path="/admin/numbers/:dnisId"
              element={
                <Protected requireKind="Admin">
                  <AdminNumberEditPage />
                </Protected>
              }
            />
            <Route
              path="/admin/permission-groups"
              element={
                <Protected requireKind="Admin">
                  <PermissionGroupsPage />
                </Protected>
              }
            />
            <Route
              path="/admin/managers"
              element={
                <Protected requireKind="Admin">
                  <ManagersAdminPage />
                </Protected>
              }
            />
            <Route
              path="/admin/managers/:managerId"
              element={
                <Protected requireKind="Admin">
                  <ManagerEditPage />
                </Protected>
              }
            />
            <Route
              path="/admin/reports"
              element={
                <Protected requireKind="Admin">
                  <ReportsAdminPage />
                </Protected>
              }
            />
            <Route
              path="/admin/reports/:reportName"
              element={
                <Protected requireKind="Admin">
                  <ReportEditPage />
                </Protected>
              }
            />
            <Route
              path="/ippbx/extensions"
              element={<Protected requireAnyKind={["Admin", "Manager"]}><PbxExtensionsPage /></Protected>}
            />
            <Route
              path="/ippbx/extensions/:agentId"
              element={<Protected requireAnyKind={["Admin", "Manager"]}><PbxExtensionDetailPage /></Protected>}
            />
            <Route
              path="/ippbx/banlist"
              element={<Protected requireAnyKind={["Admin", "Manager"]}><PbxBanlistPage /></Protected>}
            />
            <Route
              path="/ippbx/routing"
              element={
                <Protected requireAnyKind={["Admin", "Manager"]}>
                  <PbxRoutingPage />
                </Protected>
              }
            />
            {/* TEMPORARY: Fonix entry history migration. */}
            <Route
              path="/admin/fonix-entry-import"
              element={
                <Protected requireKind="Admin">
                  <FonixEntryImportPage />
                </Protected>
              }
            />
            {/* The brand page became the managers page. */}
            <Route path="/admin/brand" element={<Navigate to="/admin/managers" replace />} />
            <Route
              path="/admin/aiagents"
              element={
                <Protected requireKind="Admin">
                  <AiAgentsAdminPage />
                </Protected>
              }
            />
            <Route
              path="/admin/recognizers"
              element={
                <Protected requireKind="Admin">
                  <RecognizersAdminPage />
                </Protected>
              }
            />
            {/* Legacy redirects: keep old /agents bookmarks working. */}
            <Route
              path="/agents"
              element={<Navigate to="/destinations" replace />}
            />
            <Route
              path="/agents/:agentId"
              element={<NavigateLegacyAgent />}
            />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </main>
        {!bare && <Footer />}
      </div>
    </SessionProvider>
  );
}

import { useParams } from "react-router-dom";
import { useSession } from "./hooks/useSession";

/**
 * The root has no page of its own: signed-in users go to the dashboard
 * (which bounces admins and managers to their own landing), everyone
 * else to the sign-in page.
 */
function HomeRedirect() {
  const { session, loading } = useSession();
  if (loading) return null;
  return <Navigate to={session?.isAuthenticated ? "/dashboard" : "/login"} replace />;
}

function NavigateLegacyAgent() {
  const { agentId } = useParams<{ agentId: string }>();
  return <Navigate to={`/destinations/${agentId ?? ""}`} replace />;
}

/**
 * Admin sessions don't have a customer context, so the regular
 * customer-side dashboard would render an empty/broken state for them.
 * Bounce admins to their own landing instead so they only ever see
 * pages that match their role.
 */
function DashboardOrAdminLanding() {
  const { session } = useSession();
  // Neither admins nor managers have a customer context of their own,
  // so bounce both to the (scoped) customers overview instead of the
  // customer-side dashboard.
  if (session?.kind === "Admin" || session?.kind === "Manager") {
    return <Navigate to="/admin/customers" replace />;
  }
  return <DashboardPage />;
}
